import { useEffect, useState } from "react";
import axios from "axios";
import {
  Menu,
  X,
  ArrowUpRight,
  Volume2,
  VolumeX,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Phone,
} from "lucide-react";
import ReviewCarousel from "@/components/ReviewCarousel";
import BookingCalendar from "@/components/BookingCalendar";
import InstagramReels from "@/components/InstagramReels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIDEO =
  "https://res.cloudinary.com/xflarfw0/video/upload/v1787844155/Recording_2026-08-27_205151.mp4";
const ADDRESS =
  "No 8, First Floor, MA Complex, NGO Nagar Main Rd, Alapakkam, New Perungalathur, Chennai, Nedunkundram, Tamil Nadu 600063, India";
const NAV = [
  ["WORK", "work"],
  ["SERVICES", "services"],
  ["ALBUM", "album"],
  ["ABOUT", "about"],
  ["REVIEWS", "reviews"],
  ["CONTACT", "contact"],
];
const CATEGORY_TAGLINES = {
  Wedding: "WHERE LOVE BECOMES LEGACY",
  Couples: "YOUR STORY, BEAUTIFULLY TOLD",
  "Baby Shower": "THE SMALLEST MOMENTS, FOREVER",
  Birthday: "ANOTHER YEAR, ANOTHER STORY",
};

function Header({ logo, open, setOpen }) {
  const go = (id) => {
    if (id === "album") {
      window.location.href = "/album";
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setOpen(false);
  };
  return (
    <header className="site-header">
      <a className="brand" href="#top" data-testid="brand-home-link" aria-label="Infinite Photography home">
        {logo ? (
          <img
            src={logo}
            alt="Infinite Photography logo"
            className="brand-logo"
          />
        ) : (
          <span className="wordmark">
            INFINITE
            <span>PHOTOGRAPHY</span>
          </span>
        )}
      </a>
      <nav className={open ? "open" : ""}>
        {NAV.map(([label, id]) => (
          <button
            key={id}
            onClick={() => go(id)}
            data-testid={`nav-${id}-link`}
          >
            {label}
          </button>
        ))}
        <button
          className="header-cta"
          onClick={() => go("contact")}
          data-testid="nav-plan-shoot-button"
        >
          PLAN YOUR SHOOT <ArrowUpRight size={15} />
        </button>
      </nav>
      <button
        className="icon-btn menu-btn"
        onClick={() => setOpen(!open)}
        data-testid="mobile-menu-button"
        aria-label="Toggle menu"
      >
        {open ? <X /> : <Menu />}
      </button>
    </header>
  );
}

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  shoot_type: "",
  venue: "",
  preferred_time: "",
  message: "",
};

export default function Home() {
  const [site, setSite] = useState(null);
  const [siteError, setSiteError] = useState(false);
  const [open, setOpen] = useState(false);
  const [sound, setSound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [dates, setDates] = useState([]);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const fetchSite = () => {
    setSiteError(false);
    axios
      .get(`${API}/site`, { params: { t: Date.now() } })
      .then((r) => setSite(r.data))
      .catch(() => setSiteError(true));
  };

  useEffect(() => {
    fetchSite();
  }, []);

  if (siteError)
    return (
      <div className="loading" data-testid="site-error">
        We couldn't load the site right now.
        <button
          onClick={fetchSite}
          data-testid="site-error-retry-button"
          style={{ marginLeft: 12 }}
        >
          Retry
        </button>
      </div>
    );

  if (!site)
    return (
      <div className="loading" data-testid="site-loading">
        Loading the frames…
      </div>
    );

  const photos = (site.photos || []).slice(0, 10);
  const contact = site.contact || {};
  const about = site.about || {};
  const logo = site.branding?.logo || contact.logo;
  const telHref = `tel:${String(contact.phone || "").replace(/[^\d+]/g, "")}`;
  const mapsHref =
    contact.google_maps ||
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      "Infinite Photography, " + (contact.address || ADDRESS),
    )}`;
  const appleHref =
    contact.apple_maps ||
    `https://maps.apple.com/?address=${encodeURIComponent(contact.address || ADDRESS)}`;
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.shoot_type) {
      setError("Please choose the type of shoot.");
      return;
    }
    if (dates.length === 0) {
      setError("Please choose at least one preferred date.");
      return;
    }
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        shoot_type: form.shoot_type,
        requested_dates: dates,
        venue: form.venue || "To be confirmed",
        preferred_time: form.preferred_time || "To be confirmed",
      });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send request");
    }
  };

  return (
    <div id="top">
      <section className="hero">
        <video
          className="hero-video"
          autoPlay
          muted={!sound}
          loop
          playsInline
          poster={site.hero?.poster || photos[0]?.url}
          data-testid="hero-video"
        >
          <source src={site.hero?.video || VIDEO} type="video/mp4" />
        </video>
        <div className="hero-shade" />
        <Header logo={site.branding?.logo || site.contact?.logo} open={open} setOpen={setOpen} />
        <div className="hero-copy">
          <p className="eyebrow">INFINITE PHOTOGRAPHY · CHENNAI</p>
          <h1>
            Moments pass.
            <br />
            <em>Memories don’t.</em>
          </h1>
          <p className="hero-intro">
            Photography created around the people, celebrations and little
            moments you’ll want to remember forever.
          </p>
          <div className="rating">
            ★★★★★ <span>4.9 GOOGLE RATING · 94 REVIEWS</span>
          </div>
          <div className="hero-actions">
            <button
              className="gold-btn"
              onClick={() =>
                document
                  .getElementById("contact")
                  .scrollIntoView({ behavior: "smooth" })
              }
              data-testid="hero-plan-shoot-button"
            >
              PLAN YOUR SHOOT <ArrowUpRight size={16} />
            </button>
            <button
              className="line-btn"
              onClick={() =>
                document
                  .getElementById("work")
                  .scrollIntoView({ behavior: "smooth" })
              }
              data-testid="hero-explore-work-button"
            >
              EXPLORE OUR WORK
            </button>
          </div>
        </div>
        <button
          className="sound-control"
          onClick={() => setSound(!sound)}
          data-testid="hero-sound-toggle"
        >
          {sound ? <Volume2 size={16} /> : <VolumeX size={16} />}{" "}
          {sound ? "SOUND ON" : "HEAR THE MOMENT"}
        </button>
        <div className="scroll-cue">
          SCROLL <span>↓</span>
        </div>
      </section>

      <section className="story-band">
        <p className="eyebrow">TELL US YOUR STORY</p>
        <h2>
          What are we <em>capturing?</em>
        </h2>
        <div className="category-grid four">
          {site.categories.map((c, i) => (
            <article
              className="category"
              key={c.id || c.name}
              data-testid={`category-${(c.id || c.name || "").toLowerCase().replaceAll(" ", "-")}-card`}
            >
              {c.image ? (
                <img src={c.image} alt={c.alt || `${c.title || c.name} photography`} />
              ) : (
                <div className="category-placeholder">
                  <span>Add your photo</span>
                </div>
              )}
              <div>
                <span>0{i + 1}</span>
                <h3>{c.title || c.name}</h3>
                <p>
                  {c.tagline ||
                    CATEGORY_TAGLINES[c.title || c.name] ||
                    "MOMENTS WORTH KEEPING"}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="work" className="portfolio section">
        <div className="section-head">
          <div>
            <p className="eyebrow">PORTFOLIO</p>
            <h2>
              Moments,
              <br />
              <em>beautifully kept.</em>
            </h2>
          </div>
          <p className="section-note">
            A quiet collection of the seconds before, between, and after.
          </p>
        </div>
        {photos.length === 0 ? (
          <p className="section-note" data-testid="portfolio-empty">
            New photographs are being curated.
          </p>
        ) : (
          <div className="editorial-grid" data-testid="editorial-grid">
            {photos.map((p, i) => (
              <button
                className={`ed-frame ed-frame-${i + 1}`}
                key={p.id}
                onClick={() => setLightbox(i)}
                data-testid={`portfolio-image-${i + 1}`}
              >
                <img src={p.url} alt={p.title || `Frame ${i + 1}`} loading="lazy" />
                <span>{String(i + 1).padStart(2, "0")} / VIEW FRAME</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {site.featured?.image && (
      <section className="featured section">
        <div className="featured-photo">
          <img src={site.featured.image} alt={site.featured.title} />
        </div>
        <div className="featured-copy">
          <p className="eyebrow">FEATURED FRAME · {site.featured.label}</p>
          <h2>{site.featured.title}</h2>
          <p>{site.featured.caption}</p>
          <button
            className="text-link"
            onClick={() => setLightbox(Math.min(3, photos.length - 1))}
            data-testid="featured-frame-button"
          >
            VIEW FRAME <ArrowUpRight size={16} />
          </button>
        </div>
      </section>
      )}

      <section id="album" className="album-strip section">
        <div>
          <p className="eyebrow">THE FULL COLLECTION</p>
          <h2>
            THE <em>ARCHIVE</em>
          </h2>
        </div>
        <a
          className="round-arrow"
          href="/album"
          data-testid="album-scroll-button"
          aria-label="Open the album"
        >
          <ArrowUpRight />
        </a>
      </section>

      <section id="services" className="services section">
        <p className="eyebrow">OUR CRAFT</p>
        <h2>
          What shall
          <br />
          <em>we capture?</em>
        </h2>
        <div className="service-list">
          {site.services.map((s, i) => (
            <div className="service-row" key={s.title || i} data-testid={`service-${i + 1}`}>
              <span>◆</span>
              <h3>{s.title}</h3>
              <p>{s.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="about" className="about section">
        <div className="about-copy">
          <p className="eyebrow">MEET THE PERSON BEHIND THE CAMERA</p>
          <h2>
            {about.title ? (
              about.title
            ) : (
              <>
                Mr. <em>Vignesh</em>
              </>
            )}
          </h2>
          <p>
            {about.text ||
              "Professional, polite, patient and attentive — with a friendly approach to every celebration and every requested change."}
          </p>
        </div>
        <div className="about-photo">
          <img
            src={about.image || photos[7]?.url || photos[0]?.url}
            alt={about.title || "Mr. Vignesh photographer portrait"}
          />
        </div>
      </section>

      <section id="reviews" className="reviews-section section">
        <div className="reviews-head">
          <div className="review-score">
            <span>★★★★★</span>
            <strong>
              4.9 <small>/ 5</small>
            </strong>
            <p>94 GOOGLE REVIEWS</p>
          </div>
          <div className="review-copy">
            <p className="eyebrow">KIND WORDS</p>
            <h2>
              Real stories,
              <br />
              <em>real people.</em>
            </h2>
            <p className="review-note">
              Genuine feedback from Infinite Photography clients.
            </p>
          </div>
        </div>
        <ReviewCarousel reviews={site.reviews || []} />
      </section>

      <InstagramReels
        reels={site.reels || []}
        photos={photos}
        profileUrl={contact.instagram}
      />

      <section id="contact" className="contact section">
        <div className="contact-heading">
          <p className="eyebrow">MAKE IT YOURS</p>
          <h2>
            Tell us what
            <br />
            <em>you’re imagining.</em>
          </h2>
          <div className="contact-details">
            <a href={telHref} data-testid="contact-call-link">
              <Phone size={16} />
              {contact.phone}
            </a>
            <a
              href={mapsHref}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-google-maps-link"
            >
              <MapPin size={16} />
              GOOGLE MAPS
            </a>
            <a
              href={appleHref}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-apple-maps-link"
            >
              <MapPin size={16} />
              APPLE MAPS
            </a>
          </div>
          <address className="contact-address" data-testid="contact-address">
            <strong>INFINITE PHOTOGRAPHY</strong>
            <br />
            {(contact.address || ADDRESS).split(", ").reduce((rows, part, i) => {
              const row = Math.floor(i / 2);
              rows[row] = rows[row] ? `${rows[row]}, ${part}` : part;
              return rows;
            }, []).map((line, i) => (
              <span key={i}>
                {line}
                <br />
              </span>
            ))}
            {contact.hours && (
              <span data-testid="contact-hours">{contact.hours}</span>
            )}
          </address>
        </div>

        <form
          className="booking-form"
          onSubmit={submit}
          data-testid="booking-form"
        >
          {sent ? (
            <div className="success" data-testid="booking-success">
              Request received.
              <br />
              <span>
                Infinite Photography will confirm availability and contact you
                shortly.
              </span>
            </div>
          ) : (
            <>
              <label>
                CHOOSE YOUR SHOOT
                <select
                  required
                  value={form.shoot_type}
                  onChange={(e) =>
                    setForm({ ...form, shoot_type: e.target.value })
                  }
                  data-testid="booking-shoot-type"
                >
                  <option value="">Select a moment</option>
                  <option>Wedding</option>
                  <option>Engagement</option>
                  <option>Couple / Pre-Wedding</option>
                  <option>Baby Shower</option>
                  <option>Birthday</option>
                  <option>Family Shoot</option>
                  <option>Event Coverage</option>
                  <option>Other</option>
                </select>
              </label>

              <div className="calendar-block">
                <p className="eyebrow">CHOOSE YOUR DATE(S)</p>
                <BookingCalendar
                  availability={site.availability || []}
                  selected={dates}
                  onChange={setDates}
                  testIdPrefix="booking-cal"
                />
              </div>

              <div className="form-row">
                <label>
                  NAME
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="booking-name-input"
                  />
                </label>
                <label>
                  PHONE
                  <input
                    required
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                    data-testid="booking-phone-input"
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  EMAIL
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    data-testid="booking-email-input"
                  />
                </label>
                <label>
                  PREFERRED TIME
                  <input
                    value={form.preferred_time}
                    onChange={(e) =>
                      setForm({ ...form, preferred_time: e.target.value })
                    }
                    data-testid="booking-time-input"
                  />
                </label>
              </div>
              <label>
                VENUE / LOCATION
                <input
                  value={form.venue}
                  onChange={(e) => setForm({ ...form, venue: e.target.value })}
                  data-testid="booking-venue-input"
                />
              </label>
              <label>
                MESSAGE
                <textarea
                  placeholder="Tell us what you're imagining."
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                  data-testid="booking-message-input"
                />
              </label>
              {error && (
                <p className="error" data-testid="booking-error">
                  {error}
                </p>
              )}
              <button className="gold-btn" data-testid="booking-submit-button">
                REQUEST YOUR DATE(S) <ArrowUpRight size={16} />
              </button>
            </>
          )}
        </form>
      </section>

      <footer>
        <a className="footer-logo" href="#top" data-testid="footer-logo-link" aria-label="Infinite Photography home">
          {logo && <img src={logo} alt="Infinite Photography logo" />}
        </a>
        <div className="footer-meta">
          <p data-testid="footer-location">
            {contact.footer_note ||
              "New Perungalathur · Chennai · Tamil Nadu · 600063"}
          </p>
          {contact.phone && (
            <p>
              <a href={telHref} data-testid="footer-call-link">
                {contact.phone}
              </a>
            </p>
          )}
        </div>
        <a href="/admin/login" className="admin-link" data-testid="admin-login-link">
          OWNER LOGIN
        </a>
      </footer>

      {lightbox !== null && (
        <div className="lightbox" data-testid="portfolio-lightbox">
          <button
            onClick={() => setLightbox(null)}
            data-testid="lightbox-close-button"
            aria-label="Close"
          >
            <X />
          </button>
          <button
            onClick={() =>
              setLightbox((lightbox + photos.length - 1) % photos.length)
            }
            data-testid="lightbox-prev-button"
            aria-label="Previous"
          >
            <ChevronLeft />
          </button>
          <img src={photos[lightbox].url} alt={photos[lightbox].title} />
          <button
            onClick={() => setLightbox((lightbox + 1) % photos.length)}
            data-testid="lightbox-next-button"
            aria-label="Next"
          >
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  );
}
